import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Button,
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Snackbar,
  MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import EmailIcon from '@material-ui/icons/Email';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ScheduleIcon from '@material-ui/icons/Schedule';
import SendIcon from '@material-ui/icons/Send';
import axios from 'axios';
import InputMask from 'react-input-mask';
import { toastr } from 'react-redux-toastr';
import {
  BASE_API,
  USER_TOKEN,
  COMPANY_STATION_ID,
  GENERAL_TEXT_COLOR,
  COMPANY_COLORS,
} from 'config/consts';
import DOMPurify from 'dompurify';
import { DomPurifyContentContainer } from './styles';

const primaryColor = COMPANY_COLORS?.primary || '#2D95EF';

const useStyles = makeStyles(() => ({
  root: {
    padding: '0 0.5rem',
  },
  formCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: '1.25rem',
    boxShadow: '0 1px 8px rgba(0,0,0,0.08)',
    marginBottom: '1.5rem',
  },
  formTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: '1rem',
    color: GENERAL_TEXT_COLOR || '#333',
  },
  field: {
    marginBottom: '0.75rem',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
    },
  },
  submitBtn: {
    textTransform: 'none',
    borderRadius: 8,
    padding: '10px 24px',
    fontWeight: 600,
    fontSize: '0.95rem',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: '0.75rem',
    color: GENERAL_TEXT_COLOR || '#333',
  },
  referralCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: '1rem 1.25rem',
    marginBottom: '0.75rem',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    border: '1px solid #f0f0f0',
    transition: 'box-shadow 0.2s',
    '&:hover': {
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    },
  },
  referralHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  referralName: {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: '#333',
  },
  referralEmail: {
    fontSize: '0.8rem',
    color: '#888',
    marginTop: 2,
  },
  statusChip: {
    fontWeight: 600,
    fontSize: '0.7rem',
  },
  actionsRow: {
    display: 'flex',
    gap: 8,
    marginTop: '0.75rem',
    flexWrap: 'wrap',
  },
  actionBtn: {
    textTransform: 'none',
    borderRadius: 20,
    fontSize: '0.8rem',
    padding: '4px 14px',
  },
  linkRow: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#fafafa',
    border: '1px solid #e8e8e8',
    borderRadius: 8,
    padding: '6px 10px',
    marginTop: 8,
    gap: 6,
  },
  linkText: {
    flex: 1,
    fontSize: 11,
    color: '#666',
    wordBreak: 'break-all',
    fontFamily: 'monospace',
  },
  sentBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: '0.75rem',
    color: '#4caf50',
    marginTop: 6,
  },
  emptyState: {
    textAlign: 'center',
    padding: '2rem 1rem',
    color: '#999',
  },
  counterBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: primaryColor,
    color: '#fff',
    borderRadius: '50%',
    width: 22,
    height: 22,
    fontSize: '0.75rem',
    fontWeight: 700,
    marginLeft: 8,
  },
}));

const statusConfig = {
  pending: { label: 'Pendente', color: '#ff9800', icon: ScheduleIcon },
  registered: { label: 'Cadastrado', color: '#2196f3', icon: ScheduleIcon },
  affiliated: { label: 'Filiado', color: '#9c27b0', icon: ScheduleIcon },
  completed: { label: 'Concluída', color: '#4caf50', icon: CheckCircleIcon },
};

function ActivityReferral(props) {
  const classes = useStyles();
  const [invitedName, setInvitedName] = useState('');
  const [invitedEmail, setInvitedEmail] = useState('');
  const [invitedPhone, setInvitedPhone] = useState('');
  const [house, setHouse] = useState('');
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const [justSent, setJustSent] = useState({});

  const actionId = props.activities.detail.id;
  const userId = props.auth.user.id;
  const userName = props.auth.user.name;

  useEffect(() => {
    if (!actionId) return;
    axios
      .get(`${BASE_API}/referral-campaign/${actionId}`, {
        headers: {
          Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
        },
      })
      .then((res) => {
        const raw = res.data?.houses;
        if (!raw) return;
        try {
          const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (Array.isArray(parsed)) {
            const names = parsed
              .map((h) => (typeof h === 'string' ? h : h?.name))
              .filter(Boolean);
            setHouses(names);
          }
        } catch (e) {
          // ignore
        }
      })
      .catch(() => {});
  }, [actionId]);

  const fetchReferrals = async () => {
    setLoadingReferrals(true);
    try {
      const response = await axios.get(`${BASE_API}/referrals/user/${userId}`, {
        headers: {
          Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
        },
      });
      const filtered = response.data.filter((r) => r.action_id === actionId);
      setReferrals(filtered);
    } catch (e) {
      console.error('Erro ao buscar indicações:', e);
    }
    setLoadingReferrals(false);
  };

  useEffect(() => {
    if (actionId && userId) fetchReferrals();
  }, [actionId, userId]);

  const sendInviteForReferral = async (referral) => {
    const channels = {
      email: !!referral.invited_email,
      whatsapp: !!referral.invited_phone,
    };
    await axios.post(
      `${BASE_API}/send-referral-invite`,
      {
        referral_id: referral.id,
        station_id: COMPANY_STATION_ID,
        sender_name: userName,
        invited_name: referral.invited_name,
        invited_email: referral.invited_email,
        invited_phone: referral.invited_phone,
        referral_link: referral.referral_link,
        channels,
      },
      {
        headers: {
          Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
        },
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!invitedName || !invitedEmail) {
      toastr.warning('Atenção', 'Preencha o nome e e-mail do indicado.');
      return;
    }
    const phoneDigits = (invitedPhone || '').replace(/\D/g, '');
    if (phoneDigits.length !== 11 || phoneDigits[2] !== '9') {
      toastr.warning(
        'Atenção',
        'Informe um WhatsApp válido do indicado (DDD + 9 dígitos).'
      );
      return;
    }
    if (houses.length > 0 && !house) {
      toastr.warning('Atenção', 'Selecione a sua casa.');
      return;
    }
    setLoading(true);
    try {
      const createResponse = await axios.post(
        `${BASE_API}/referrals`,
        {
          action_id: actionId,
          invited_name: invitedName,
          invited_email: invitedEmail,
          invited_phone: invitedPhone,
          house: houses.length > 0 ? house : undefined,
        },
        {
          headers: {
            Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
          },
        }
      );

      const newReferral = createResponse.data;

      try {
        await sendInviteForReferral({
          ...newReferral,
          invited_name: invitedName,
          invited_email: invitedEmail,
          invited_phone: invitedPhone,
        });
        const sentVia = [];
        if (invitedEmail) sentVia.push('e-mail');
        if (invitedPhone) sentVia.push('WhatsApp');
        toastr.success(
          'Indicação enviada!',
          `Convite enviado por ${sentVia.join(' e ')} para ${invitedName}.`
        );
      } catch (sendErr) {
        console.error('Erro ao enviar convite automático:', sendErr);
        toastr.success(
          'Indicação criada!',
          'Indicação registrada. Envie o convite manualmente abaixo.'
        );
      }

      setInvitedName('');
      setInvitedEmail('');
      setInvitedPhone('');
      setHouse('');
      fetchReferrals();
    } catch (error) {
      const msg = error.response?.data?.error || 'Erro ao criar indicação';
      toastr.error('Erro', msg);
    }
    setLoading(false);
  };

  const getReferralFullLink = (referralLink) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/#/indicacao/${referralLink}`;
  };

  const handleCopyLink = (referralLink) => {
    const link = getReferralFullLink(referralLink);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => setSnackOpen(true));
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = link;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setSnackOpen(true);
    }
  };

  const handleSendInvite = async (referral, channel) => {
    const key = `${referral.id}-${channel}`;
    setSendingInvite(key);
    try {
      const channels = {
        email: channel === 'email',
        whatsapp: channel === 'whatsapp',
      };
      await axios.post(
        `${BASE_API}/send-referral-invite`,
        {
          referral_id: referral.id,
          station_id: COMPANY_STATION_ID,
          sender_name: userName,
          invited_name: referral.invited_name,
          invited_email: referral.invited_email,
          invited_phone: referral.invited_phone,
          referral_link: referral.referral_link,
          channels,
        },
        {
          headers: {
            Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
          },
        }
      );
      setJustSent((prev) => ({ ...prev, [key]: true }));
      toastr.success(
        'Enviado!',
        channel === 'email'
          ? `Convite reenviado por e-mail para ${referral.invited_name}!`
          : `Convite reenviado por WhatsApp para ${referral.invited_name}!`
      );
      setTimeout(() => {
        setJustSent((prev) => ({ ...prev, [key]: false }));
      }, 3000);
    } catch (error) {
      toastr.error('Erro', 'Não foi possível enviar o convite.');
    }
    setSendingInvite(null);
  };

  return (
    <Grid container justifyContent="center" xs={12} className={classes.root}>
      <Grid item xs={12} style={{ marginBottom: '1rem' }}>
        <DomPurifyContentContainer
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(props.activities.detail.description),
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <div className={classes.formCard}>
          <div className={classes.formTitle}>
            <PersonAddIcon style={{ color: primaryColor }} />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Indicar alguém
            </Typography>
          </div>
          <form onSubmit={handleSubmit}>
            {houses.length > 0 && (
              <TextField
                select
                className={classes.field}
                fullWidth
                variant="outlined"
                size="small"
                label="Sua casa"
                value={house}
                onChange={(e) => setHouse(e.target.value)}
                required
              >
                {houses.map((h) => (
                  <MenuItem key={h} value={h}>
                    {h}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              className={classes.field}
              fullWidth
              variant="outlined"
              size="small"
              label="Nome do indicado"
              value={invitedName}
              onChange={(e) => setInvitedName(e.target.value)}
              required
            />
            <TextField
              className={classes.field}
              fullWidth
              variant="outlined"
              size="small"
              label="E-mail do indicado"
              type="email"
              value={invitedEmail}
              onChange={(e) => setInvitedEmail(e.target.value)}
              required
            />
            <InputMask
              mask="(99) 99999-9999"
              value={invitedPhone}
              onChange={(e) => setInvitedPhone(e.target.value)}
            >
              {(inputProps) => (
                <TextField
                  {...inputProps}
                  className={classes.field}
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="WhatsApp do indicado"
                  type="tel"
                  required
                />
              )}
            </InputMask>
            <Button
              className={classes.submitBtn}
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={18} style={{ color: '#fff' }} />
                ) : (
                  <SendIcon />
                )
              }
            >
              {loading ? 'Enviando...' : 'Indicar e enviar convite'}
            </Button>
          </form>
        </div>
      </Grid>

      <Grid item xs={12}>
        <div className={classes.sectionTitle}>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Minhas indicações
          </Typography>
          {referrals.length > 0 && (
            <span className={classes.counterBadge}>{referrals.length}</span>
          )}
        </div>

        {loadingReferrals ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <CircularProgress size={28} />
          </div>
        ) : referrals.length === 0 ? (
          <div className={classes.emptyState}>
            <PersonAddIcon
              style={{ fontSize: 48, color: '#ddd', marginBottom: 8 }}
            />
            <Typography variant="body2" color="textSecondary">
              Nenhuma indicação realizada ainda.
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Preencha o formulário acima para indicar alguém.
            </Typography>
          </div>
        ) : (
          referrals.map((referral) => {
            const status =
              statusConfig[referral.status] || statusConfig.pending;
            return (
              <div key={referral.id} className={classes.referralCard}>
                <div className={classes.referralHeader}>
                  <div style={{ flex: 1 }}>
                    <div className={classes.referralName}>
                      {referral.invited_name}
                    </div>
                    <div className={classes.referralEmail}>
                      {referral.invited_email}
                    </div>
                  </div>
                  <Chip
                    size="small"
                    label={status.label}
                    className={classes.statusChip}
                    style={{
                      backgroundColor: `${status.color}18`,
                      color: status.color,
                      border: `1px solid ${status.color}40`,
                    }}
                  />
                </div>

                <div className={classes.actionsRow}>
                  {referral.invited_email && (
                    <Button
                      size="small"
                      variant="outlined"
                      className={classes.actionBtn}
                      startIcon={
                        justSent[`${referral.id}-email`] ? (
                          <CheckCircleIcon style={{ color: '#4caf50' }} />
                        ) : (
                          <EmailIcon />
                        )
                      }
                      disabled={sendingInvite === `${referral.id}-email`}
                      onClick={() => handleSendInvite(referral, 'email')}
                      style={
                        justSent[`${referral.id}-email`]
                          ? { borderColor: '#4caf50', color: '#4caf50' }
                          : {}
                      }
                    >
                      {sendingInvite === `${referral.id}-email`
                        ? 'Enviando...'
                        : justSent[`${referral.id}-email`]
                        ? 'Enviado!'
                        : 'Reenviar e-mail'}
                    </Button>
                  )}
                  {referral.invited_phone && (
                    <Button
                      size="small"
                      variant="outlined"
                      className={classes.actionBtn}
                      startIcon={
                        justSent[`${referral.id}-whatsapp`] ? (
                          <CheckCircleIcon style={{ color: '#4caf50' }} />
                        ) : (
                          <WhatsAppIcon />
                        )
                      }
                      disabled={sendingInvite === `${referral.id}-whatsapp`}
                      onClick={() => handleSendInvite(referral, 'whatsapp')}
                      style={
                        justSent[`${referral.id}-whatsapp`]
                          ? { borderColor: '#4caf50', color: '#4caf50' }
                          : { borderColor: '#25D366', color: '#25D366' }
                      }
                    >
                      {sendingInvite === `${referral.id}-whatsapp`
                        ? 'Enviando...'
                        : justSent[`${referral.id}-whatsapp`]
                        ? 'Enviado!'
                        : 'Reenviar WhatsApp'}
                    </Button>
                  )}
                </div>

                <div className={classes.linkRow}>
                  <span className={classes.linkText}>
                    {getReferralFullLink(referral.referral_link)}
                  </span>
                  <IconButton
                    size="small"
                    onClick={() => handleCopyLink(referral.referral_link)}
                  >
                    <FileCopyIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </div>
              </div>
            );
          })
        )}
      </Grid>

      <Snackbar
        open={snackOpen}
        autoHideDuration={2000}
        onClose={() => setSnackOpen(false)}
        message="Link copiado!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Grid>
  );
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  activities: state.activities,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ActivityReferral);
